<script setup>
import { ref, watch, onMounted, nextTick, computed } from 'vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {getTranslation} from '@/ts/utilities.ts'
import FocusTrap from "@/components/helpers/FocusTrap.vue";

// Props
const props = defineProps({
	modelValue: { type: Boolean, default: false },
	title: { type: String, default: '' },
	position: { type: String, default: 'left' }, // 'left' | 'right'
	width: { type: String, default: 'w-80' }, // Tailwind width class, e.g. w-64, w-80, w-96
	closeOnEscape: { type: Boolean, default: true },
	closeOnOverlayClick: { type: Boolean, default: true },
	ariaLabel: { type: String, default: 'Slide out menu' },
})

const emit = defineEmits(['update:modelValue', 'opened', 'closed'])
const panel = ref(null)

const close = () => {
	emit('update:modelValue', false)
}

const onOverlayClick = () => {
	if (props.closeOnOverlayClick) close()
}

function onTrapKeydown(event) {
	if (event.key === 'Escape') {
		close()
	}
}

watch(() => props.modelValue, async (val) => {
	const cookiebotWidget = document.getElementById('CookiebotWidget')
	const emaerketWidget = document.getElementById('emaerket-widget-container')
	if (val) {
		emit('opened')
		await nextTick()
		panel.value?.focus()
		document.documentElement.style.overflow = 'hidden'
		if (cookiebotWidget) {
			cookiebotWidget.style.display = 'none'
		}
		if (emaerketWidget) {
			emaerketWidget.style.display = 'none'
		}

	} else {
		emit('closed')
		document.documentElement.style.overflow = ''
		if (cookiebotWidget) {
			cookiebotWidget.style.display = 'block'
		}
		if (emaerketWidget) {
			emaerketWidget.style.display = 'block'
		}
	}
})

onMounted(() => {
	if (!props.modelValue) document.documentElement.style.overflow = ''
})



const panelBaseClasses = 'fixed top-0 bottom-0 z-50 bg-white shadow-xl flex flex-col focus:outline-none'
const positionClasses = computed(() => props.position === 'right' ? 'right-0' : 'left-0')
const widthClass = computed(() => props.width)
const panelTransitionName = computed(() => props.position === 'right' ? 'slide-right' : 'slide-left')
</script>

<template>
	<div>
		<!-- Overlay -->
		<transition name="fade">
			<div
				v-if="modelValue"
				class="fixed inset-0 z-40 bg-black opacity-50"
				@click="onOverlayClick"
				aria-hidden="true"
			></div>
		</transition>

		<!-- Slide-out panel -->
		<transition :name="panelTransitionName">
			<aside
				v-if="modelValue"
				:class="[panelBaseClasses, positionClasses, widthClass, 'overflow-auto']"
				role="dialog"
				aria-modal="true"
				:aria-label="ariaLabel"
				@keydown.esc.prevent="close"
				ref="panel"
			>
				<focus-trap :active="modelValue" :onKeydown="onTrapKeydown">
				<div class="flex items-center justify-between py-2 px-4 bg-beige">
					<h3 class="text-lg font-semibold" v-if="title">{{ title }}</h3>
					<button
						@click="close"
						aria-label="Close menu"
						class="p-2 rounded-md hover:bg-slate-100 ml-auto"
					>
						<span class="mr-2 hidden lg:inline-flex">
							{{ getTranslation('Close') }}
						</span>
						<font-awesome-icon :icon="['fal', 'close']" />
					</button>
				</div>

				<div class="p-4 overflow-auto flex-1 hide-scrollbar">
					<slot></slot>
				</div>
				</focus-trap>
			</aside>
		</transition>
	</div>
</template>



<style scoped>
/* transitions (Tailwind handles most, but we add a couple for translate) */
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.slide-left-enter-active { transition: transform .25s ease-out; }
.slide-left-leave-active { transition: transform .2s ease-in; }
.slide-left-enter-from { transform: translateX(-100%); }
.slide-left-enter-to { transform: translateX(0%); }
.slide-left-leave-from { transform: translateX(0%); }
.slide-left-leave-to { transform: translateX(-100%); }

.slide-right-enter-active { transition: transform .25s ease-out; }
.slide-right-leave-active { transition: transform .2s ease-in; }
.slide-right-enter-from { transform: translateX(100%); }
.slide-right-enter-to { transform: translateX(0%); }
.slide-right-leave-from { transform: translateX(0%); }
.slide-right-leave-to { transform: translateX(100%); }
/* Hide scrollbar but maintain scrolling functionality */
.hide-scrollbar {
	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
	display: none; /* Chrome, Safari, and Opera */
}

</style>
